import type { BrandKit } from '@goldcast/api/backend';
import { getFileNameFromPath, getFileNameWithoutExtension } from '@goldcast/player-config/src/utils/fileUtils';
import type { PlatformFontEntry } from '@goldcast/player-config/src/types/brand-kit-types';
import {
  getFontEntriesFromPlatformBrandKitPure,
  isFontStylesheetUrl
} from '@goldcast/player-config/src/utils/fontUtils';
import { currentUser } from '@/stores/user';

/** True when `url` looks like a direct font binary (ttf/otf/woff/woff2/eot); not for CSS import URLs. */
export function isFontFileUrl(url: string): boolean {
  return /\.(ttf|otf|woff2?|eot)(?:[?#]|$)/i.test(url);
}

/** `{ url → name }` for FontFace; omits stylesheet URLs and bare CSS family names (use `injectStylesheetFonts` for stylesheets; system fonts need no loading). */
export function buildFontFaceMap(entries: PlatformFontEntry[]): Record<string, string> {
  return entries.reduce<Record<string, string>>((acc, { url, name }) => {
    if (url && (url.startsWith('http://') || url.startsWith('https://')) && !isFontStylesheetUrl(url)) {
      acc[url] = name;
    }
    return acc;
  }, {});
}

/**
 * Injects a single `<link rel="stylesheet">` for a web font CSS URL and returns a promise
 * that resolves once loaded (or immediately if already injected).
 */
export function loadStylesheetFont(url: string): Promise<string> {
  if (typeof document === 'undefined') {
    return Promise.resolve(url);
  }
  const id = `webfont-${btoa(url)
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 40)}`;
  if (document.getElementById(id)) {
    return Promise.resolve(url);
  }
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = url;
    link.onload = () => resolve(url);
    link.onerror = () => reject(new Error(`Failed to load web font stylesheet: ${url}`));
    document.head.appendChild(link);
  });
}

/** Appends deduped `<link rel="stylesheet">` tags for each stylesheet URL in `entries`. */
export function injectStylesheetFonts(entries: PlatformFontEntry[]): void {
  for (const { url } of entries) {
    if (!isFontStylesheetUrl(url)) {
      continue;
    }
    const id = `bk-webfont-${btoa(url)
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 40)}`;
    if (document.getElementById(id)) {
      continue;
    }
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }
}

/**
 * Display label for `fontLocation`: match platform kit, then default fonts, else basename, else `fallbackName`.
 */
export function resolveFontDisplayName(
  fontLocation: string | undefined,
  platformEntries: PlatformFontEntry[],
  defaultFonts: ReadonlyArray<{ url: string; name: string }>,
  fallbackName: string
): string {
  if (!fontLocation) {
    return fallbackName;
  }
  const platformEntry = platformEntries.find(e => e.url === fontLocation);
  if (platformEntry) {
    return platformEntry.name;
  }
  const defaultEntry = defaultFonts.find(f => f.url === fontLocation);
  if (defaultEntry) {
    return defaultEntry.name;
  }
  const base = getFileNameFromPath(fontLocation);
  return (base.includes('.') ? getFileNameWithoutExtension(base) : base) || fallbackName;
}

/**
 * Deduplicated `{ url, name }` list from `kit.fonts` (structured slots) then `kit.cl_fonts`; safe for picker UI, FontFace, and stylesheet injection.
 */
export function getFontEntriesFromPlatformBrandKit(kit: BrandKit | undefined): PlatformFontEntry[] {
  return getFontEntriesFromPlatformBrandKitPure(kit, currentUser.getSnapshot()?.organization);
}
